import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary.tsx'
import { initGA4, initPostHog } from './lib/analytics.ts'
import { ignoreErrors, denyUrls, beforeSend } from './lib/sentry-filters.ts'
import { TelemetryProvider } from './core/telemetry/TelemetryProvider.tsx'
import { primateTelemetry } from './primate/telemetry-bridge.ts'

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Mask user-uploaded content and prompt inputs before going to prod-at-scale
        mask: ['[data-sentry-mask]', '.prompt-input', '.upload-filename', '[data-user-content]'],
        block: ['video', 'canvas'],
      }),
    ],
    tracesSampleRate: 0.1, // 10% of transactions
    replaysSessionSampleRate: 0.0, // 0% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% on errors
    environment: import.meta.env.PROD ? 'production' : 'development',
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || 'dev',
    // Noise reduction: third-party scripts (crypto-wallet extensions, the
    // Intercom messenger widget) throw errors on our page that are not ours.
    // All filtering logic + rationale lives in src/lib/sentry-filters.ts.
    ignoreErrors,
    denyUrls,
    beforeSend,
  })
}

initGA4()
void initPostHog()

// Recover from stale-chunk loads after a deploy (Sentry: "'text/html' is not a
// valid JavaScript MIME type for module script"). Each deploy replaces the
// hashed asset filenames; a client holding a pre-deploy index.html requests an
// old chunk, Vercel's SPA rewrite serves index.html (text/html) for the missing
// file, and the dynamic import throws. Vite fires 'vite:preloadError' for this
// exact case — one reload fetches the fresh index.html + matching chunks.
// sessionStorage guard prevents a reload loop if the reload doesn't heal it.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'primate:chunk-reload-at'
  const last = Number(sessionStorage.getItem(KEY) || 0)
  if (Date.now() - last < 30_000) return // already reloaded recently; don't loop
  sessionStorage.setItem(KEY, String(Date.now()))
  event.preventDefault() // suppress the unhandled-rejection noise
  window.location.reload()
})

// Prevent Clerk's anti-flash (visibility:hidden on body) from breaking Playwright tests.
// Only active on localhost — no effect on production.
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  const _forceBodyVisible = () => {
    if (document.body && document.body.style.visibility === 'hidden') {
      document.body.style.visibility = 'visible'
    }
    if (document.documentElement && document.documentElement.style.visibility === 'hidden') {
      document.documentElement.style.visibility = 'visible'
    }
  }
  // Watch for Clerk setting visibility:hidden and immediately revert it
  const _clerkAntiFlashObserver = new MutationObserver(_forceBodyVisible)
  const _observeBody = () => {
    _clerkAntiFlashObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] })
    _clerkAntiFlashObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })
  }
  if (document.body) {
    _observeBody()
  } else {
    document.addEventListener('DOMContentLoaded', _observeBody)
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary>
      {/* R14 (PRI-439): inject the first-party telemetry impl into core's seam */}
      <TelemetryProvider telemetry={primateTelemetry}>
        <ErrorBoundary>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ErrorBoundary>
      </TelemetryProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
